import { addMinutes, differenceInCalendarDays, format, parse, startOfDay } from 'date-fns';
import { chain, find, forEach, isEmpty, isNull } from 'lodash';
import {
  ARMENIAN_TIME_ZONE_HOURS,
  CONTRACT_END_ISO_TIME,
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATE_FORMAT_WITH_TIME,
  YEREVAN_CITY_VILLAGE,
} from '../../constants';
import { PACKAGES, TRAVEL_PLANS, TRAVEL_PRICE_KEYS } from '../../constants/travelCalcConstants';
import { getLimitByValueAndCurrency, getRegionFromDestinations } from './travelCalcHelpers';
import mapBackendErrorsToUserFields, { mapBackendErrorsToCalculatorFields } from '../../validations/backendErrors';
import { convertUserToApiFormat, convertUserToStoreFormat } from '../contractHelpers';
import { getArmenianLocalDate, isToday, replaceTimePortion, replaceTimeZone } from '../dates';
import { findIsYerevan } from '../countryHelper';

const getTravelersWithPromoCodes = (travelers, policyPrices, selectedPlan) => {
  const policyPrice = policyPrices[selectedPlan];
  if (!policyPrice || !policyPrice.discountedPrices) {
    return travelers;
  }
  return travelers.map((trav) => {
    const { discountedPrices } = policyPrice;
    const discount = discountedPrices[trav.passport] || {};
    if (discount.promoCodeApply) {
      return { ...trav, promoCode: discount.promoCode };
    }
    return trav;
  });
};

export const convertContractToApiFormat = (contract) => {
  const signer = convertUserToApiFormat(contract.signer);

  const convertedContract = { ...signer };

  const { travelDetails } = contract;

  // We pass empty string to {socCard}, Masys throws exception when {socCard} isn't exists
  const travelers = contract.travelers.map((traveler) => ({
    ...traveler,
    socCard: traveler.socCard || '',
  }));

  const convertedTravelers = travelers.map((traveler) => convertUserToApiFormat(traveler));

  convertedContract.travelers = getTravelersWithPromoCodes(
    convertedTravelers,
    travelDetails.policyPrices,
    travelDetails.selectedPlan,
  );

  convertedContract.destinations = travelDetails.selectedDestinations;

  let startDate = travelDetails.range.start;
  if (isToday(startDate)) {
    startDate = addMinutes(new Date(getArmenianLocalDate()), 10);
  } else {
    startDate = startOfDay(travelDetails.range.start);
  }
  convertedContract.contractStart = startDate && replaceTimeZone(
    format(startDate, DATE_FORMAT, new Date(getArmenianLocalDate())),
    ARMENIAN_TIME_ZONE_HOURS,
  );

  convertedContract.contractEnd = travelDetails.range.end && replaceTimePortion(replaceTimeZone(
    format((travelDetails.range.end), DATE_FORMAT, new Date(getArmenianLocalDate())),
    ARMENIAN_TIME_ZONE_HOURS,
  ), CONTRACT_END_ISO_TIME);

  convertedContract.contractDays = travelDetails.days;
  convertedContract.selectedPackage = travelDetails.selectedPackage;
  convertedContract.region = travelDetails.region;
  convertedContract.plan = {};
  convertedContract.plan.type = travelDetails.selectedPlan;
  convertedContract.plan.selected = travelDetails.selectedLimit;
  const selectedPlanCoverages = TRAVEL_PLANS[travelDetails.selectedPlan]?.coverages ?? [];

  convertedContract.plan.coverages = Object.entries(
    travelDetails.selectedCoverages,
  ).reduce((acc, [key, value]) => {
    if (Object.prototype.hasOwnProperty.call(selectedPlanCoverages, key)) {
      acc.push(value);
    }
    return acc;
  }, []);
  convertedContract.prices = travelDetails.prices;
  convertedContract.type = 'travel';

  if (contract.draftId) {
    convertedContract.draftId = contract.draftId;
  }

  if (
    findIsYerevan(convertedContract.contactInfo.state)
      && isNull(convertedContract.contactInfo.cityVillage)
  ) {
    convertedContract.contactInfo.cityVillage = YEREVAN_CITY_VILLAGE;
  }

  return convertedContract;
};

export const convertContractToStoreFormat = (
  contract,
  dateFormat = DATE_FORMAT,
  contractDateFormat = DATE_FORMAT,
) => {
  const {
    birthday,
    citizenship,
    firstName,
    lastName,
    firstNameEn,
    lastNameEn,
    gender,
    passport,
    passportGiven,
    passportValidUntil,
    passportFrom,
    resident,
    socCard,
    contactInfo,
    memberId,
  } = contract;

  const signer = convertUserToStoreFormat(
    {
      birthday,
      citizenship,
      firstName,
      lastName,
      firstNameEn,
      lastNameEn,
      gender,
      passport,
      passportGiven,
      passportValidUntil,
      passportFrom,
      resident,
      socCard,
      contactInfo,
      memberId,
    },
    dateFormat,
  );

  const travelers = contract.travelers
    && contract.travelers.map((trav) => {
      const converted = convertUserToStoreFormat(trav, dateFormat);
      if (converted.memberId == null) {
        const isSigner = converted.passport === passport;

        // memberId can be 0 for signer
        converted.memberId = !isSigner ? converted.id : 0;
      }
      return converted;
    });

  const {
    destinations,
    contractDays,
    contractStart,
    contractEnd,
    plan,
    selectedPackage,
    region,
    draftId,
  } = contract;

  const selectedCoverages = plan
    && plan.coverages
    && plan.coverages.reduce((coverage, value) => {
      const cov = { ...coverage };
      cov[value.id] = value;
      return cov;
    }, {});

  const travelDetails = {
    selectedDestinations: destinations,
    days: contractDays,
    range: {
      start: contractStart && parse(contractStart, contractDateFormat, new Date()),
      end: contractEnd && parse(contractEnd, contractDateFormat, new Date()),
    },
    selectedPlan: plan && plan.type,
    selectedCoverages,
    selectedLimit: plan && plan.selected,
    selectedPackage,
    birthday: signer.birthday,
    prices: {},
    region,
    policyPrices: {},
  };

  const hasAddedSigner = !!travelers.find((traveler) => traveler.memberId === 0);
  const promoCodes = travelers.reduce((acc, traveler, index) => {
    if (traveler.promoCode) {
      return [
        ...acc,
        {
          policyNumber: traveler.promoCode,
          passport: traveler.passport,
          socialCard: traveler.socCard,
          memberId: index,
        },
      ];
    }
    return acc;
  }, []);

  return {
    signer,
    travelers,
    travelDetails,
    draftId,
    hasAddedSigner,
    promoCodes: promoCodes.length ? promoCodes : null,
  };
};

export const convertSignedContractToStoreFormat = (contract) => {
  const limit = getLimitByValueAndCurrency(contract.limit, contract.currency);
  const coverageCodes = contract.coverages.map((coverage) => coverage.code);
  const coverages = Object.values(TRAVEL_PLANS.ELITE.coverages).map((coverage) => ({
    id: coverage.id,
    active: coverageCodes.includes(coverage.id),
  }));

  const plan = {
    type: contract.plan,
    selected: limit,
    coverages,
  };

  const start = parse(contract.contractStart, DATE_FORMAT_WITH_TIME, new Date());
  const end = parse(contract.contractEnd, DATE_FORMAT_WITH_TIME, new Date());
  const diff = differenceInCalendarDays(end, start) + 1;
  const isPackage = diff !== contract.contractDays;
  const destinations = contract.destinations.map((item) => item.destination);
  const region = getRegionFromDestinations(destinations);

  const convertedContract = {
    ...contract,
    plan,
    destinations,
    region,
  };

  if (isPackage) {
    convertedContract.selectedPackage = find(PACKAGES, (p) => p.value === contract.contractDays);
  }

  return convertContractToStoreFormat(convertedContract, DATE_FORMAT_SHORT, DATE_FORMAT_WITH_TIME);
};

export const convertSuggestionToStoreFormat = (s) => convertUserToStoreFormat(s, DATE_FORMAT_SHORT);

const travelerErrorsRegex = /(?<travelers>^travelers)\.(?<index>\d+)\.(?<property>.*$)/;

export function mapErrorsToContractSteps(errors, contractData) {
  let travelerBackendErrors = {};
  let signerBackendErrors;
  let calculatorBackendErrors;
  let nonTravelerErrors = [];

  forEach(errors, (error, key) => {
    const travelersErrorGroups = key.match(travelerErrorsRegex);

    if (travelersErrorGroups) {
      const travelerIndex = +travelersErrorGroups.groups.index;
      const { memberId } = contractData.travelers[travelerIndex];
      if (travelerBackendErrors[memberId]) {
        travelerBackendErrors[memberId].push(error);
      } else {
        travelerBackendErrors[memberId] = [];
        travelerBackendErrors[memberId].push(error);
      }
    } else {
      nonTravelerErrors.push(error);
    }
  });

  nonTravelerErrors = chain(nonTravelerErrors)
    .flatten()
    .uniq()
    .value();

  // signer traveler errors removed as it cant be edited in travelers step
  delete travelerBackendErrors[0];
  forEach(travelerBackendErrors, (errorList, tId) => {
    const travelerErrors = chain(errorList)
      .flatten()
      .uniq()
      .value();
    travelerBackendErrors[tId] = mapBackendErrorsToUserFields(travelerErrors);
  });

  signerBackendErrors = mapBackendErrorsToUserFields(nonTravelerErrors);
  calculatorBackendErrors = mapBackendErrorsToCalculatorFields(
    nonTravelerErrors,
  );

  if (isEmpty(signerBackendErrors)) {
    signerBackendErrors = null;
  }
  if (isEmpty(travelerBackendErrors)) {
    travelerBackendErrors = null;
  }
  if (isEmpty(calculatorBackendErrors)) {
    calculatorBackendErrors = null;
  }

  return {
    travelerBackendErrors,
    signerBackendErrors,
    calculatorBackendErrors,
  };
}

export const getTotalPrice = (travelDetails) => {
  const { policyPrices, selectedPlan } = travelDetails;
  if (!selectedPlan) {
    return 0;
  }

  const planPrices = travelDetails.prices
    && travelDetails.prices[selectedPlan]
    && travelDetails.prices[selectedPlan]
    && Object.values(travelDetails.prices[selectedPlan]);
  const price = planPrices
    && planPrices.reduce((acc, el) => acc + el[TRAVEL_PRICE_KEYS.SUM], 0);

  const policyPrice = policyPrices[selectedPlan];
  const discountedPrices = policyPrice
    && policyPrice.discountedPrices
    && Object.values(policyPrice.discountedPrices);
  if (discountedPrices && !!discountedPrices.length) {
    const discount = discountedPrices.reduce((acc, el) => acc + el.totalSaving, 0);
    return price - discount;
  }
  return price;
};
